import {
  PageConfig,
  PageConfigSchemaVersion,
} from "../../config/pageConfig.types";
import { DefaultFormId } from "../../config/resolveView";
import { defaultRelatedTables } from "../../config/defaultRelatedTables";
import {
  fullWidth,
  halfWidth,
  thirdWidth,
} from "../../blocks/layout/columnSpans";

/** The Contact page's shipped layout, before any view is authored over it. */
export const contactPageConfig: PageConfig = {
  schemaVersion: PageConfigSchemaVersion,
  entityName: "contact",
  viewId: DefaultFormId,
  pageType: "record",
  rows: [
    {
      id: "row-main",
      columns: [
        {
          id: "column-primary",
          span: { desktop: 9, tablet: 12, mobile: 12 },
          blocks: [
            {
              kind: "section",
              id: "section-personal-details",
              title: "Personal Details",
              helperText: "Basic identifying details for this contact.",
              fields: [
                {
                  id: "field-salutation",
                  type: "text",
                  name: "salutation",
                  label: "Title",
                  span: thirdWidth,
                },
                {
                  id: "field-firstname",
                  type: "text",
                  name: "firstname",
                  label: "First Name",
                  required: true,
                  span: thirdWidth,
                },
                {
                  id: "field-lastname",
                  type: "text",
                  name: "lastname",
                  label: "Last Name",
                  required: true,
                  span: thirdWidth,
                },
                {
                  id: "field-emailaddress1",
                  type: "email",
                  name: "emailaddress1",
                  label: "Email Address",
                  span: thirdWidth,
                },
                {
                  id: "field-gendercode",
                  type: "radio",
                  name: "gendercode",
                  label: "Gender",
                  span: thirdWidth,
                  options: [
                    { label: "Male", value: 1 },
                    { label: "Female", value: 2 },
                    { label: "Prefer not to say", value: null },
                  ],
                  inline: true,
                },
                {
                  id: "field-birthdate",
                  type: "date",
                  name: "birthdate",
                  label: "Date of Birth",
                  // Narrower than a third: birthdate and age pair up on one line.
                  span: { desktop: 2, tablet: 6, mobile: 6 },
                  onChange: { behaviour: "calculateAge", target: "g4_age" },
                },
                {
                  id: "field-g4_age",
                  type: "number",
                  name: "g4_age",
                  label: "Age",
                  readOnly: true,
                  span: { desktop: 2, tablet: 6, mobile: 6 },
                },
                {
                  id: "field-telephone1",
                  type: "text",
                  name: "telephone1",
                  label: "Business Phone",
                  span: thirdWidth,
                },
                {
                  id: "field-telephone2",
                  type: "text",
                  name: "telephone2",
                  label: "Home Phone",
                  span: thirdWidth,
                },
                {
                  id: "field-mobilephone",
                  type: "text",
                  name: "mobilephone",
                  label: "Mobile Phone",
                  span: thirdWidth,
                },
              ],
            },
            {
              kind: "section",
              id: "section-primary-address",
              title: "Primary Address",
              helperText: "The contact's main address.",
              fields: [
                {
                  id: "field-address1_line1",
                  type: "text",
                  name: "address1_line1",
                  label: "Line 1",
                  span: thirdWidth,
                },
                {
                  id: "field-address1_line2",
                  type: "text",
                  name: "address1_line2",
                  label: "Line 2",
                  span: thirdWidth,
                },
                {
                  id: "field-address1_city",
                  type: "text",
                  name: "address1_city",
                  label: "City",
                  span: thirdWidth,
                },
                {
                  id: "field-address1_county",
                  type: "text",
                  name: "address1_county",
                  label: "County",
                  span: thirdWidth,
                },
                {
                  id: "field-address1_country",
                  type: "text",
                  name: "address1_country",
                  label: "Country",
                  span: thirdWidth,
                },
                {
                  id: "field-address1_postalcode",
                  type: "text",
                  name: "address1_postalcode",
                  label: "Postal Code",
                  span: thirdWidth,
                },
              ],
            },
          ],
        },
        {
          id: "column-secondary",
          span: { desktop: 3, tablet: 12, mobile: 12 },
          // Preferences and Record Status are one run of settings about this contact,
          // so they share a card and it runs the height of the details beside it.
          card: true,
          fillHeight: true,
          blocks: [
            {
              kind: "section",
              id: "section-marketing-preferences",
              title: "Marketing Preferences",
              helperText: "Controls what this contact receives from the club.",
              fields: [
                {
                  id: "field-donotbulkemail",
                  type: "boolean",
                  name: "donotbulkemail",
                  label: "Club Email",
                  span: halfWidth,
                },
                {
                  id: "field-g4c_donotallowsms",
                  type: "boolean",
                  name: "g4c_donotallowsms",
                  label: "Club SMS",
                  span: halfWidth,
                },
                {
                  id: "field-donotbulkpostalmail",
                  type: "boolean",
                  name: "donotbulkpostalmail",
                  label: "Club Postal Mail",
                  span: halfWidth,
                },
                {
                  id: "field-donotphone",
                  type: "boolean",
                  name: "donotphone",
                  label: "Club Phone Calls",
                  span: halfWidth,
                },
              ],
            },
            {
              kind: "section",
              id: "section-partner-preferences",
              title: "Partner Preferences",
              helperText:
                "Controls what this contact receives from the club's partners.",
              fields: [
                {
                  id: "field-g4_donotbulkemail3rdparty",
                  type: "boolean",
                  name: "g4_donotbulkemail3rdparty",
                  label: "Partner Email",
                  span: halfWidth,
                },
                {
                  id: "field-g4_donotsms3rdparty",
                  type: "boolean",
                  name: "g4_donotsms3rdparty",
                  label: "Partner SMS",
                  span: halfWidth,
                },
                {
                  id: "field-g4_donotpostalmail3rdparty",
                  type: "boolean",
                  name: "g4_donotpostalmail3rdparty",
                  label: "Partner Postal Mail",
                  span: halfWidth,
                },
                {
                  id: "field-g4_donotphone3rdparty",
                  type: "boolean",
                  name: "g4_donotphone3rdparty",
                  label: "Partner Phone Calls",
                  span: halfWidth,
                },
              ],
            },
            {
              kind: "section",
              id: "section-record-status",
              title: "Record Status",
              helperText: "",
              fields: [
                {
                  id: "field-g4_deceased",
                  type: "checkbox",
                  name: "g4_deceased",
                  label: "Deceased",
                  span: fullWidth,
                },
              ],
            },
          ],
        },
      ],
    },
    {
      id: "row-tables",
      columns: [
        {
          id: "column-tables",
          span: fullWidth,
          blocks: [
            {
              kind: "tables",
              id: "block-related-tables",
              tabs: defaultRelatedTables.contact,
            },
          ],
        },
      ],
    },
  ],
};
